import { useSearchEmployees } from '@hooks/useEmployee'
import {
  useSearchDayLocks,
  useUpsertDayLocks,
  useDeleteDayLocks,
} from '../hooks/useDayLocks'
import { useSendToPayroll } from '../hooks/useSendToPayroll'
import { useAdminTimeCards } from '@hooks/useTimeCards'
import { useWeekDates } from '@hooks/useWeekDates'
import { useWeekStartsOn } from '@hooks/useWeekStartsOn'
import {
  FilterWorkerssWithVisibleItemsCounts,
  FilterWorkerWithVisibleItemsButtonGroup,
  ResetButton,
  StatusMiniSilos,
} from 'components/buttons'
import { FC, useMemo, useRef, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { DayLockType, Status, WorkdaySendStatus } from 'types'
import { AlertButtons } from '../components/AlertButtons'
import { Header } from '../components/Header'
import { Sidebar } from '../components/sidebar'
import { ImperativeHandle, Table } from '../components/Table'
import { useSelectedCells } from '../hooks/useSelectedCells'
import {
  FilterAlertTypeCount,
  LocksDictionary,
  SelectedEmployeeValues,
  StatusCounts,
  ViewingDetails,
  WorkerAndTimeCards,
} from '../types'
import { buildLockPayloadData } from '../utils/buildLockPayloadData'
import { timeCardsFromSelected } from '../utils/timeCardsFromSelected'
import { getStatusCount } from '../utils/getStatusCount'
import { isEmployee } from '@utils/workers'
import { useModal, usePendingModal } from '@hooks/useModal'
import { toast } from '@lib/toasts'
import { DayLockPayload } from '@lib/api'
import { useFiltering } from '../hooks/useFiltering'
import { WeekSelectorSubHeader } from 'components/page-header'
import { Divider } from 'components/filter-bar'
import {
  AnomalousTimeCardButtons,
  AnomalyCounts,
} from 'components/buttons/AnomalousTimeCardButtons'
import { useSubmissionsAdminLocalStorage } from '../hooks/useSubmissionsAdminLocalStorage'

const massageLocks = (locks: DayLock[] = []): LocksDictionary => {
  return locks.reduce<LocksDictionary>((acc, lock) => {
    return {
      ...acc,
      [lock.workdayWorkerId]: {
        ...acc[lock.workdayWorkerId],
        [lock.date.toMillis()]: lock.type,
      },
    }
  }, {})
}

export const SubmissionsAdmin: FC = () => {
  const { t } = useTranslation()
  const modal = useModal()
  const { open: openPendingModal } = usePendingModal()

  const {
    hiddenDays,
    hiddenStatuses,
    facilityIds,
    approvalGroupIds,
    filterEmployeesWithVisibleTimeCards,
    sorting,
    filteredAlertTypes,
    filteredAnomalyTypes,
    employeeNameFilter,
    clear: clearFiltersAndSorting,
    isChanged: localStorageIsChanged,
  } = useSubmissionsAdminLocalStorage()

  const weekStartsOn = useWeekStartsOn()
  const {
    start: selectedWeek,
    set: setSelectedWeek,
    range,
    end: endOfWeek,
  } = useWeekDates({ weekStartsOn })
  const {
    clear: clearSelected,
    selected,
    select,
    unSelect,
  } = useSelectedCells()

  const [employeeSidebarOpened, setEmployeeSidebarOpened] = useState(false)
  const [viewingDetails, setViewDetails] = useState<ViewingDetails>()

  const { filters, applyFilters, filtersWithout } = useFiltering({
    hiddenDays: hiddenDays.value,
    filteredAlertTypes: filteredAlertTypes.value,
    filteredAnomalyTypes: filteredAnomalyTypes.value,
    hiddenStatuses: hiddenStatuses.value,
    filterEmployeesWithVisibleTimeCards:
      filterEmployeesWithVisibleTimeCards.value,
    employeeNameFilter: employeeNameFilter.debounced,
    filteredFacilityIds: facilityIds.debounced,
    filteredApprovalGroupIds: approvalGroupIds.value,
  })

  const {
    data: timeCards,
    refetch: refetchTimeCards,
    isLoading: isLoadingTimeCards,
  } = useAdminTimeCards(selectedWeek, endOfWeek)
  const { data: employees, isLoading: isLoadingEmployees } = useSearchEmployees(
    selectedWeek,
    endOfWeek,
  )
  const locksQuery = useSearchDayLocks(selectedWeek, endOfWeek)

  const isLoading = useMemo(
    () => isLoadingTimeCards || isLoadingEmployees,
    [isLoadingEmployees, isLoadingTimeCards],
  )

  const upsertLocks = useUpsertDayLocks()
  const deleteLocks = useDeleteDayLocks()
  const sendTimeCardsToPayroll = useSendToPayroll()

  // This is used as a convenient way to:
  // 1. Identify when *any* filter has changed
  // 2. Identify if *any* filters are enabled (or even how many)
  const enabledFilters = useMemo(
    () => ({
      hiddenDays: hiddenDays.value.size > 0 ? hiddenDays.value : false,
      hiddenStatuses:
        hiddenStatuses.value.size > 0 ? hiddenStatuses.value : false,
      filteredAlertTypes:
        filteredAlertTypes.value.size > 0 ? filteredAlertTypes.value : false,
      employeeNameFilter:
        employeeNameFilter.value !== '' ? employeeNameFilter.value : false,
      filteredFacilityIds:
        facilityIds.value.size > 0 ? facilityIds.value : false,
      filteredApprovalGroupIds:
        approvalGroupIds.value.size > 0 ? approvalGroupIds.value : false,
      filterEmployeesWithVisibleTimeCards:
        filterEmployeesWithVisibleTimeCards.value === 'all'
          ? false
          : filterEmployeesWithVisibleTimeCards.value,
      filterAnomalyTypes:
        filteredAnomalyTypes.value.size > 0
          ? filteredAnomalyTypes.value
          : false,
    }),
    [
      hiddenDays.value,
      hiddenStatuses.value,
      filteredAlertTypes.value,
      filteredAnomalyTypes.value,
      employeeNameFilter.value,
      facilityIds.value,
      approvalGroupIds.value,
      filterEmployeesWithVisibleTimeCards.value,
    ],
  )

  // Clear selection when any filters change
  useEffect(() => clearSelected(), [enabledFilters, clearSelected])

  const employeesAndTimeCards = useMemo<WorkerAndTimeCards[]>(() => {
    if (!employees || !timeCards) return []

    const map: { [key: number]: WorkerAndTimeCards } = {}

    employees.forEach((worker) => {
      map[worker.workdayWorkerId] = {
        worker,
        timeCards: [],
      }
    })

    timeCards.forEach((timeCard) => {
      if (!map[timeCard.workdayWorkerId]) {
        throw new Error(
          `Time Card (${timeCard.id}) found for worker not returned by search (${timeCard.workdayWorkerId})`,
        )
      }

      map[timeCard.workdayWorkerId].timeCards.push(timeCard)
    })

    return Object.values(map)
  }, [employees, timeCards])

  const filteredEmployeesAndTimeCards = useMemo(
    () => applyFilters(employeesAndTimeCards, filters),
    [employeesAndTimeCards, filters, applyFilters],
  )

  const employeesWithVisibleTimeCardsFilterCounts =
    useMemo<FilterWorkerssWithVisibleItemsCounts>(() => {
      const filteredWithoutVisibleTimeCardFilter = applyFilters(
        employeesAndTimeCards,
        filtersWithout('visibleTimeCardFilter'),
      )

      const all = filteredWithoutVisibleTimeCardFilter.length
      const none = filteredWithoutVisibleTimeCardFilter.filter(
        ({ timeCards }) => timeCards.length === 0,
      ).length
      const atLeastOne = all - none

      return { all, atLeastOne, none }
    }, [employeesAndTimeCards, applyFilters, filtersWithout])

  const filterAlertTypeCounts = useMemo<FilterAlertTypeCount>(() => {
    const counts = {
      issues: 0,
      pending: 0,
      workdayError: 0,
    }

    const filteredWithoutAlertFilter = applyFilters(
      employeesAndTimeCards,
      filtersWithout('alertFilter'),
    )

    // We use statusFilteredTimeCardsWithoutAlertFilters here because
    // we want the totals for each alert after applying
    // all other filters but without applying the alert filter itself
    // (so the alert totals remain visible even when filtered out by another alert filter)
    filteredWithoutAlertFilter.forEach(({ timeCards }) => {
      timeCards.forEach((timeCard) => {
        if (timeCard.issues.length > 0) counts.issues++
        if (timeCard.workdaySendStatus === WorkdaySendStatus.Pending)
          counts.pending++
        if (timeCard.workdaySendStatus === WorkdaySendStatus.Error)
          counts.workdayError++
      })
    })

    return counts
  }, [employeesAndTimeCards, filtersWithout, applyFilters])

  const filterAnomalyTypeCounts = useMemo<AnomalyCounts>(() => {
    const counts = {
      NoBreak: 0,
      HasTaggedTime: 0,
    }

    const filteredWithoutAnomalyFilter = applyFilters(
      employeesAndTimeCards,
      filtersWithout('anomalyFilter'),
    )

    // We use filteredWithoutAnomalyFilter here because
    // we want the totals for each alert after applying
    // all other filters but without applying the anomaly filter itself
    // (so the anomaly totals remain visible even when filtered out by another anomaly filter)
    filteredWithoutAnomalyFilter.forEach(({ timeCards }) => {
      timeCards.forEach((timeCard) => {
        if (timeCard.totalSecondsTagged > 0) counts.HasTaggedTime++
        if (timeCard.totalBreaks === 0) counts.NoBreak++
      })
    })

    return counts
  }, [employeesAndTimeCards, filtersWithout, applyFilters])

  // We use alertFilteredTimeCards here because that's
  // the collection status filters filter down and we want pre-filtered
  // totals to be displayed for each status
  const statusCounts = useMemo<StatusCounts>(() => {
    const filteredWithoutStatusFilter = applyFilters(
      employeesAndTimeCards,
      filtersWithout('statusFilter'),
    )

    return getStatusCount(
      filteredWithoutStatusFilter.flatMap(({ timeCards }) => timeCards),
    )
  }, [applyFilters, employeesAndTimeCards, filtersWithout])

  const employeesAndTimeCardsBeforeTableFiltering = useMemo(
    () =>
      applyFilters(
        employeesAndTimeCards,
        filtersWithout('workerAttributeFilter'),
      ),
    [filtersWithout, applyFilters, employeesAndTimeCards],
  )

  const locks = useMemo(() => massageLocks(locksQuery.data), [locksQuery.data])

  const statusSelectedCounts = Object.values(selected).reduce<StatusCounts>(
    (acc, { dates: _dates, ...timeCards }: SelectedEmployeeValues) => {
      const count = { ...acc }
      Object.values(timeCards).forEach((timeCard) => {
        count[timeCard.status] = count[timeCard.status] + 1
      })
      return count
    },
    {
      [Status.Open]: 0,
      [Status.Approved]: 0,
      [Status.Sent]: 0,
      [Status.Submitted]: 0,
    },
  )

  const sendableSelectedTimeCards = useMemo(
    () =>
      timeCardsFromSelected(selected).filter(
        (timeCard) =>
          timeCard.status === Status.Approved &&
          !timeCard.correction &&
          timeCard.issues.length === 0,
      ),
    [selected],
  )

  const isResettable = useMemo(
    () => localStorageIsChanged || Object.keys(selected).length > 0,
    [localStorageIsChanged, selected],
  )

  // Refetch Time Cards every 5s if any are sent and pending a response from WD
  useEffect(() => {
    if (
      timeCards?.every(
        (timeCard) =>
          timeCard.status !== Status.Sent ||
          timeCard.workdaySendStatus !== WorkdaySendStatus.Pending,
      )
    )
      return

    const timeoutId = setTimeout(() => void refetchTimeCards(), 5000)
    return () => clearTimeout(timeoutId)
  }, [timeCards, refetchTimeCards])

  const processUpsertLocks = async ({
    days,
    type,
  }: {
    days: DayLockPayload[]
    type: DayLockType
  }) => {
    try {
      await upsertLocks.mutateAsync({ days, type })

      toast({
        title: t('features.admin.lockingSuccessful'),
        variant: 'success',
        content: t('features.admin.lockedDaysSuccessfully', {
          count: days.length,
        }),
      })
    } catch {
      modal.alert({
        content: t('features.admin.failedToLockDays'),
      })
    }
  }

  const handleLockOperation = (type: DayLockType) => () => {
    const days = buildLockPayloadData(selected)

    modal.confirm({
      title: t('features.admin.confirmLocks'),
      content: t('features.admin.confirmLockSelected', { count: days.length }),
      onConfirmAsync: async () => await processUpsertLocks({ days, type }),
    })
  }

  const processDeleteLocks = async (days: DayLockPayload[]) => {
    try {
      await deleteLocks.mutateAsync(days)

      toast({
        title: t('features.admin.unlockingSuccessful'),
        variant: 'success',
        content: t('features.admin.unlockedDaysSuccessfully', {
          count: days.length,
        }),
      })
    } catch {
      modal.alert({
        content: t('features.admin.failedToUnlockDays'),
      })
    }
  }

  const handleUnlock = () => {
    const days = buildLockPayloadData(selected)

    const dayLocks = locksQuery.data || []

    const unlockableDays = days.filter((day) =>
      dayLocks.find(
        (lock) =>
          lock.workdayWorkerId === day.workdayWorkerId &&
          lock.date.hasSame(day.date, 'day'),
      ),
    )

    modal.confirm({
      title: t('features.admin.confirmUnlock'),
      content: t('features.admin.confirmUnlockSelected', {
        count: unlockableDays.length,
      }),
      onConfirmAsync: async () => await processDeleteLocks(unlockableDays),
    })
  }

  const processSendToPayroll = async (timeCardIds: number[]) => {
    const id = openPendingModal(
      t('features.admin.sendingSelectedTimeCardsToPayroll'),
    )

    try {
      await sendTimeCardsToPayroll.mutateAsync(timeCardIds)

      // NOTE: This is critical for fixing a bug where a user is filtered
      //       on Approved, sends a TC to payroll, and then that TC
      //       is no longer visible, but is part of their selection
      clearSelected()

      toast({
        title: t('features.admin.sendSuccesful'),
        variant: 'success',
        content: t('features.admin.successfullySentTimeCardsToWorkday', {
          count: timeCardIds.length,
        }),
      })
    } catch {
      modal.alert({
        content: t('features.admin.failedToSendToPayroll'),
      })
    } finally {
      modal.close(id)
    }
  }

  const handleSendToPayroll = () => {
    modal.confirm({
      title: t('features.admin.confirmSendToPayroll'),
      content: t('features.admin.confirmSendSelectedTimeCardsToPayroll', {
        count: sendableSelectedTimeCards.length,
      }),
      onConfirm: () =>
        void processSendToPayroll(
          sendableSelectedTimeCards.map((timeCard) => timeCard.id),
        ),
    })
  }

  const reset = () => {
    clearSelected()
    clearFiltersAndSorting()
  }

  const tableRef = useRef<ImperativeHandle>(null)

  const closeSidebar = useCallback(() => {
    setEmployeeSidebarOpened(false)
    setViewDetails(undefined)
  }, [])

  const onCellFocusChange = useCallback(
    (workerAndTimeCards?: WorkerAndTimeCards, date?: DateTime) => {
      if (workerAndTimeCards === undefined || date === undefined) {
        setViewDetails(undefined)
        setEmployeeSidebarOpened(false)
      } else {
        const { worker, timeCards } = workerAndTimeCards

        setViewDetails({
          worker,
          timeCard: timeCards?.find((timeCard) =>
            timeCard.date.hasSame(date, 'day'),
          ),
          date,
        })
        setEmployeeSidebarOpened(true)
      }
    },
    [],
  )

  return (
    <div className="flex flex-grow">
      <div className="flex flex-col flex-grow">
        <Header
          lockingEnabled={Object.values(selected).length > 0}
          onFullLock={handleLockOperation(DayLockType.Full)}
          onSendToWorkday={handleSendToPayroll}
          onSubmit={() => alert('onSubmit')}
          onUnapproveLock={handleLockOperation(DayLockType.Unapprove)}
          onUnsubmit={() => alert('onUnsubmit')}
          onUnsubmitLock={handleLockOperation(DayLockType.Unsubmit)}
          onUnlock={handleUnlock}
          selections={selected}
          sendToWorkdayEnabled={sendableSelectedTimeCards.length > 0}
        />
        <WeekSelectorSubHeader
          onWeekSelect={(startOfWeek) => {
            clearSelected()
            setSelectedWeek(startOfWeek)
            closeSidebar() // Close sidebar when changing weeks
          }}
          selectedWeek={selectedWeek}
          weekStartsOn={weekStartsOn}
        >
          <FilterWorkerWithVisibleItemsButtonGroup
            counts={employeesWithVisibleTimeCardsFilterCounts}
            onChange={filterEmployeesWithVisibleTimeCards.set}
            value={filterEmployeesWithVisibleTimeCards.value}
            loading={isLoading}
          />
          <Divider />
          <AlertButtons
            counts={filterAlertTypeCounts}
            onChange={filteredAlertTypes.set}
            selected={filteredAlertTypes.value}
          />
          <Divider />
          <AnomalousTimeCardButtons
            counts={filterAnomalyTypeCounts}
            onChange={filteredAnomalyTypes.set}
            selected={filteredAnomalyTypes.value}
          />
          <Divider />
          <StatusMiniSilos
            counts={statusCounts}
            hiddenStatuses={hiddenStatuses.value}
            onHide={hiddenStatuses.add}
            onSelect={([status]) => tableRef.current?.select(status)}
            onShow={hiddenStatuses.remove}
            onUnselect={([status]) => tableRef.current?.unSelect(status)}
            selectedCounts={statusSelectedCounts}
            loading={isLoading}
          />
          <Divider />
          <ResetButton onClick={reset} disabled={!isResettable} />
        </WeekSelectorSubHeader>
        <Table
          employeesAndTimeCards={filteredEmployeesAndTimeCards}
          hiddenDays={hiddenDays.value}
          locks={locks}
          onHideDays={hiddenDays.add}
          onSelect={select}
          onShowDays={hiddenDays.remove}
          onUnSelect={unSelect}
          onUnSelectAll={clearSelected}
          ref={tableRef}
          selectedCells={selected}
          weekRange={range}
          onEmployeeNameFilterChange={employeeNameFilter.set}
          employeeNameFilter={employeeNameFilter.value}
          filteredFacilityIds={facilityIds.value}
          onFacilityFilterChange={facilityIds.set}
          filteredApprovalGroupIds={approvalGroupIds.value}
          onApprovalGroupFilterChange={approvalGroupIds.set}
          employeesAndTimeCardsBeforeTableFiltering={
            employeesAndTimeCardsBeforeTableFiltering
          }
          loading={isLoading}
          employeeSidebarOpened={employeeSidebarOpened}
          onCellFocusChange={onCellFocusChange}
          sorting={sorting.value}
          setSorting={sorting.set}
        />
      </div>
      {employeeSidebarOpened && viewingDetails && (
        <Sidebar
          date={viewingDetails.date}
          employeeDetails={{
            department: viewingDetails.worker.department.name,
            type: isEmployee(viewingDetails.worker)
              ? t(`payTypes.${viewingDetails.worker.payType}`)
              : t(
                  `contingentWorkerTypes.${viewingDetails.worker.contingentWorkerType}`,
                ),
            fullName: viewingDetails.worker.fullName,
            title: viewingDetails.worker.jobTitle || undefined,
          }}
          employeeId={viewingDetails.worker.workdayWorkerId}
          onClose={closeSidebar}
          timeCard={viewingDetails.timeCard}
        />
      )}
    </div>
  )
}
